import { useState, useEffect, useCallback } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSTextField from "views/system-design/DSTextField";
import DSPaper from "views/system-design/DSPaper";
import DSIconButton from "views/system-design/DSIconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import useStudioFaq from "../hooks/useStudioFaq";
import useSaveStudioFaq from "../hooks/useSaveStudioFaq";
import { FaqItem, FaqLink } from "api/admin";

type FaqItemDraft = FaqItem & { _key: string };

const newKey = () => crypto.randomUUID();

const emptyItem = (): FaqItemDraft => ({
  _key: newKey(),
  question: "",
  answer: "",
});
const emptyLink = (): FaqLink => ({ text: "", url: "" });

const StudioFaqEditor = () => {
  const { data, isPending: isLoading, error } = useStudioFaq();
  const { mutate: save, isPending: isSaving } = useSaveStudioFaq();
  const [items, setItems] = useState<FaqItemDraft[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  useEffect(() => {
    // Don't clobber in-progress edits when the query refetches (e.g. on
    // window refocus); only seed from the server when there's nothing unsaved.
    if (data?.items && !hasChanges) {
      setItems(data.items.map((item) => ({ ...item, _key: newKey() })));
    }
  }, [data?.items]);

  const handleFieldChange = (
    index: number,
    field: "question" | "answer",
    value: string,
  ) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
    setHasChanges(true);
  };

  const handleLinkChange = (
    itemIndex: number,
    linkIndex: number,
    field: keyof FaqLink,
    value: string,
  ) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== itemIndex) return item;
        const links = [...(item.links ?? [])];
        links[linkIndex] = { ...links[linkIndex], [field]: value };
        return { ...item, links };
      }),
    );
    setHasChanges(true);
  };

  const handleAddLink = (itemIndex: number) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== itemIndex) return item;
        return { ...item, links: [...(item.links ?? []), emptyLink()] };
      }),
    );
    setHasChanges(true);
  };

  const handleRemoveLink = (itemIndex: number, linkIndex: number) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== itemIndex) return item;
        return {
          ...item,
          links: (item.links ?? []).filter((_, li) => li !== linkIndex),
        };
      }),
    );
    setHasChanges(true);
  };

  const handleAddItem = () => {
    const item = emptyItem();
    setItems((prev) => [...prev, item]);
    setExpandedKeys((prev) => [...prev, item._key]);
    setHasChanges(true);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;

    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(source.index, 1);
      updated.splice(destination.index, 0, moved);
      return updated;
    });
    setHasChanges(true);
  }, []);

  const handleSave = () => {
    setSaveError(null);
    save(
      items.map(({ _key, ...item }) => item),
      {
        onSuccess: () => setHasChanges(false),
        onError: () => setSaveError("Failed to save FAQ. Please try again."),
      },
    );
  };

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (error) {
    return (
      <DSBox sx={{ p: 3 }}>
        <DSTypography color="error">
          Failed to load FAQ: {error.message}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DSTypography variant="h6">Studio FAQ</DSTypography>
        <DSBox sx={{ display: "flex", gap: 1 }}>
          <DSSecondaryButton onClick={handleAddItem} startIcon={<AddIcon />}>
            Add Question
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? "Saving..." : "Save FAQ"}
          </DSPrimaryButton>
        </DSBox>
      </DSBox>
      {saveError && <DSTypography color="error">{saveError}</DSTypography>}
      <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {items.length === 0 && (
          <DSTypography
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            No FAQ items yet. Click "Add Question" to get started.
          </DSTypography>
        )}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="faq-items">
            {(provided) => (
              <DSBox
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                {items.map((item, itemIndex) => {
                  const expanded = expandedKeys.includes(item._key);
                  return (
                    <Draggable
                      key={item._key}
                      draggableId={item._key}
                      index={itemIndex}
                    >
                      {(dragProvided, snapshot) => (
                        <DSPaper
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          sx={{
                            p: 2,
                            boxShadow: snapshot.isDragging ? 6 : 1,
                            bgcolor: snapshot.isDragging
                              ? "action.hover"
                              : "background.paper",
                          }}
                        >
                          <DSBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                            }}
                          >
                            <DSBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <DSBox
                                {...dragProvided.dragHandleProps}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  cursor: "grab",
                                  color: "text.secondary",
                                  mr: 1,
                                }}
                              >
                                <DragIndicatorIcon />
                              </DSBox>
                              <DSTypography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ flexShrink: 0, mr: 1 }}
                              >
                                Item {itemIndex + 1}
                              </DSTypography>
                              <DSTypography
                                variant="subtitle2"
                                color={
                                  item.question
                                    ? "text.primary"
                                    : "text.secondary"
                                }
                                onClick={() => toggleExpanded(item._key)}
                                sx={{
                                  flex: 1,
                                  cursor: "pointer",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.question || "New question"}
                              </DSTypography>
                              <DSIconButton
                                onClick={() => toggleExpanded(item._key)}
                                size="small"
                              >
                                {expanded ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </DSIconButton>
                              <DSIconButton
                                onClick={() => handleRemoveItem(itemIndex)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </DSIconButton>
                            </DSBox>
                            {expanded && (
                              <>
                                <DSTextField
                                  label="Question"
                                  value={item.question}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      itemIndex,
                                      "question",
                                      e.target.value,
                                    )
                                  }
                                  fullWidth
                                />
                                <DSTextField
                                  label="Answer"
                                  value={item.answer}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      itemIndex,
                                      "answer",
                                      e.target.value,
                                    )
                                  }
                                  fullWidth
                                  multiline
                                  rows={3}
                                />
                                <DSBox
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {(item.links ?? []).map((link, linkIndex) => (
                                    <DSBox
                                      key={linkIndex}
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        alignItems: "center",
                                      }}
                                    >
                                      <DSTextField
                                        label="Link Text"
                                        value={link.text}
                                        onChange={(e) =>
                                          handleLinkChange(
                                            itemIndex,
                                            linkIndex,
                                            "text",
                                            e.target.value,
                                          )
                                        }
                                        sx={{ flex: 1 }}
                                      />
                                      <DSTextField
                                        label="Link URL"
                                        value={link.url}
                                        onChange={(e) =>
                                          handleLinkChange(
                                            itemIndex,
                                            linkIndex,
                                            "url",
                                            e.target.value,
                                          )
                                        }
                                        sx={{ flex: 2 }}
                                      />
                                      <DSIconButton
                                        onClick={() =>
                                          handleRemoveLink(itemIndex, linkIndex)
                                        }
                                        size="small"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </DSIconButton>
                                    </DSBox>
                                  ))}
                                  <DSBox>
                                    <DSSecondaryButton
                                      onClick={() => handleAddLink(itemIndex)}
                                      startIcon={<AddIcon />}
                                      size="small"
                                    >
                                      Add Link
                                    </DSSecondaryButton>
                                  </DSBox>
                                </DSBox>
                              </>
                            )}
                          </DSBox>
                        </DSPaper>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </DSBox>
            )}
          </Droppable>
        </DragDropContext>
      </DSBox>
    </DSBox>
  );
};

export default StudioFaqEditor;
